import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, Clock, Star, BookOpen, Mail, TrendingUp, ChevronRight, Sparkles, Users, PenLine, Award, Target, Rocket, Eye, Heart } from 'lucide-react';
import { resolveImageUrl } from '../utils/image';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [authorOfMonth, setAuthorOfMonth] = useState(null);
  const [searchParams] = useSearchParams();
  const searchQ = searchParams.get('search');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [authRes, aomRes] = await Promise.all([
          axios.get(`${API}/authors`),
          axios.get(`${API}/author-of-month`),
        ]);
        setAuthors(authRes.data);
        if (aomRes.data) setAuthorOfMonth(aomRes.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const url = searchQ
          ? `${API}/search?q=${encodeURIComponent(searchQ)}`
          : `${API}/articles?status=published&limit=6`;
        const res = await axios.get(url);
        setArticles(res.data.articles || res.data);
      } catch (e) { console.error(e); }
    };
    loadArticles();
  }, [searchQ]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      toast.success('Subscribed! Check your inbox.');
      setNewsletterEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    }
    setSubscribing(false);
  };

  const estimateReadTime = (content) => {
    if (!content) return 1;
    // Strip HTML tags for accurate word count
    const textOnly = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const words = textOnly.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <SEO />
      <Navbar />

      {/* Search Results */}
      {searchQ && (
        <div className="max-w-4xl mx-auto px-6 pt-12">
          <p className="text-sm font-medium text-violet-500 uppercase tracking-widest mb-2">Search Results</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold" data-testid="search-results-title">"{searchQ}"</h2>
          <p className="text-muted-foreground mt-2">{articles.length} articles found</p>
        </div>
      )}

      {/* ═══ HERO ═══ */}
      {!searchQ && (
        <section className="hero-gradient" data-testid="hero-section">
          <div className="max-w-4xl mx-auto px-6 py-24 md:py-36 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 text-violet-700 text-sm font-medium mb-8 animate-fade-in">
              <Sparkles className="w-4 h-4" />
              <span>Expert-driven technical content</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] text-foreground animate-fade-in">
              From VMs to Pods.
              <br />
              <span className="gradient-text">Master the Cloud.</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Deep technical articles on VMware, Kubernetes, DevOps, and cloud-native architecture. Written by practitioners who build at scale.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mt-10 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <Link to="/articles">
                <Button className="btn-primary btn-press rounded-lg px-8 h-12 text-sm font-semibold" data-testid="hero-cta">
                  Start Reading <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/authors">
                <Button variant="outline" className="btn-outline-glow rounded-lg px-8 h-12 text-sm font-medium btn-press">
                  Meet Our Authors
                </Button>
              </Link>
            </div>

            {/* Minimal Stats */}
            <div className="flex flex-wrap justify-center gap-12 mt-16 pt-8 border-t border-violet-200/50 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              {[
                { value: '500+', label: 'Articles', icon: BookOpen },
                { value: '50+', label: 'Expert Authors', icon: Users },
                { value: '100K+', label: 'Monthly Readers', icon: TrendingUp },
              ].map((stat, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ LATEST ARTICLES (Minimal List) ═══ */}
      {articles.length > 0 && (
        <section className="py-16 md:py-20" data-testid="articles-section">
          <div className="max-w-4xl mx-auto px-6">
            {!searchQ && (
              <div className="flex items-center justify-between mb-10">
                <h2 className="font-heading text-2xl font-bold text-foreground">Latest Articles</h2>
                <Link to="/articles" className="text-sm font-medium text-violet-600 hover:text-violet-700 flex items-center gap-1">
                  View all <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            <div className="space-y-1">
              {articles.slice(0, 6).map((article) => (
                <Link
                  key={article.article_id}
                  to={`/article/${article.slug}`}
                  className="group flex items-center gap-4 p-4 -mx-4 rounded-lg hover:bg-violet-50/50 transition-colors"
                  data-testid={`article-${article.article_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {article.category && (
                        <span className="text-xs font-medium text-violet-500">{article.category.name}</span>
                      )}
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {article.read_time || 1} min
                      </span>
                    </div>
                    <h3 className="font-medium text-foreground group-hover:text-violet-600 transition-colors line-clamp-1">
                      {article.title}
                    </h3>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-violet-600 transition-colors shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ AUTHORS (Minimal) ═══ */}
      {!searchQ && authors.length > 0 && (
        <section className="py-16 md:py-20 border-t border-slate-100" data-testid="authors-section">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center justify-between mb-10">
              <h2 className="font-heading text-2xl font-bold text-foreground">Our Authors</h2>
              <Link to="/authors" className="text-sm font-medium text-violet-600 hover:text-violet-700 flex items-center gap-1">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {authors.slice(0, 4).map((author, idx) => (
                <Link
                  key={author.user_id}
                  to={`/author/${author.user_id}`}
                  className="group text-center"
                  data-testid={`author-${author.user_id}`}
                >
                  <div className="relative mx-auto w-16 h-16 mb-3">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white font-heading font-bold text-xl overflow-hidden shadow-lg shadow-violet-200">
                      {author.picture ? (
                        <img src={resolveImageUrl(author.picture)} alt={author.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        author.name?.charAt(0)
                      )}
                    </div>
                    {idx === 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                        <Star className="w-3 h-3 text-white fill-white" />
                      </div>
                    )}
                  </div>
                  <h4 className="font-medium text-foreground group-hover:text-violet-600 transition-colors truncate">{author.name}</h4>
                  <p className="text-xs text-muted-foreground">{author.article_count || 0} articles</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ AUTHOR OF THE MONTH ═══ */}
      {!searchQ && authorOfMonth && (
        <section className="py-16 md:py-20" data-testid="author-of-month-section">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm font-medium border border-amber-200 mb-4">
                <Award className="w-4 h-4" /> Author of the Month
              </div>
            </div>

            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 via-orange-300 to-amber-400 rounded-2xl opacity-30 group-hover:opacity-50 blur-lg transition-opacity" />
              <div className="relative bg-white rounded-2xl border border-amber-100 overflow-hidden shadow-sm">
                <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400" />
                <div className="p-8 md:p-10">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {/* Avatar */}
                    <Link to={`/author/${authorOfMonth.author?.user_id}`} className="shrink-0 group/avatar">
                      <div className="relative">
                        <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-1 shadow-lg shadow-amber-200/40 group-hover/avatar:shadow-amber-300/60 group-hover/avatar:scale-[1.03] transition-all">
                          <div className="w-full h-full rounded-[14px] bg-white flex items-center justify-center overflow-hidden">
                            {authorOfMonth.author?.picture ? (
                              <img src={resolveImageUrl(authorOfMonth.author.picture)} alt="" className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <span className="text-4xl font-heading font-bold text-amber-600">
                                {authorOfMonth.author?.name?.charAt(0)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md ring-2 ring-white">
                          <Star className="w-4 h-4 text-white fill-white" />
                        </div>
                      </div>
                    </Link>

                    {/* Info */}
                    <div className="flex-1 text-center md:text-left">
                      <Link to={`/author/${authorOfMonth.author?.user_id}`} className="inline-block hover:text-amber-700 transition-colors">
                        <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">{authorOfMonth.author?.name}</h3>
                      </Link>
                      {authorOfMonth.author?.bio && (
                        <p className="text-muted-foreground mt-2 line-clamp-2 max-w-lg">{authorOfMonth.author.bio}</p>
                      )}

                      {/* Stats */}
                      <div className="flex flex-wrap justify-center md:justify-start gap-5 mt-5">
                        {[
                          { label: 'Articles', value: authorOfMonth.article_count, icon: BookOpen, bg: 'bg-blue-50', text: 'text-blue-600' },
                          { label: 'Total Views', value: authorOfMonth.total_views?.toLocaleString(), icon: Eye, bg: 'bg-green-50', text: 'text-green-600' },
                          { label: 'Claps', value: authorOfMonth.total_claps, icon: Heart, bg: 'bg-rose-50', text: 'text-rose-600' },
                        ].map(s => (
                          <div key={s.label} className="flex items-center gap-2.5">
                            <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                              <s.icon className={`w-4 h-4 ${s.text}`} />
                            </div>
                            <div>
                              <p className="text-lg font-bold leading-tight">{s.value}</p>
                              <p className="text-xs text-muted-foreground">{s.label}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Expertise */}
                      {authorOfMonth.author?.expertise?.length > 0 && (
                        <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-4">
                          {authorOfMonth.author.expertise.slice(0, 5).map(tag => (
                            <span key={tag} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* CTA */}
                    <Link to={`/author/${authorOfMonth.author?.user_id}`} className="shrink-0">
                      <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-md shadow-amber-200/50 btn-press rounded-xl px-6 h-11" data-testid="aom-view-profile">
                        View Profile <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ BECOME AN AUTHOR CTA ═══ */}
      {!searchQ && (
        <section className="py-16 md:py-20" data-testid="become-author-section">
          <div className="max-w-4xl mx-auto px-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/10 via-cyan-500/10 to-violet-600/10 rounded-2xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 md:p-12 border border-slate-700/50">
                <div className="max-w-xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/20 text-violet-300 text-xs font-medium mb-4">
                    <Sparkles className="w-3 h-3" /> Open for Contributors
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                    Share Your Infrastructure Story
                  </h3>
                  <p className="text-slate-400 mb-6 leading-relaxed">
                    Whether you're a VMware veteran, Kubernetes enthusiast, or cloud architect — your real-world experiences 
                    can help thousands of engineers navigate their own infrastructure journeys.
                  </p>
                  <div className="flex flex-wrap gap-4 mb-6 text-sm text-slate-300">
                    <span className="flex items-center gap-2"><Award className="w-4 h-4 text-amber-400" /> Recognition</span>
                    <span className="flex items-center gap-2"><Users className="w-4 h-4 text-cyan-400" /> 100K+ readers</span>
                    <span className="flex items-center gap-2"><Target className="w-4 h-4 text-violet-400" /> Portfolio</span>
                  </div>
                  {user?.role === 'reader' ? (
                    <Link to="/apply-author">
                      <Button className="btn-primary btn-press rounded-lg px-8 h-12">
                        <PenLine className="w-4 h-4 mr-2" /> Apply to Become an Author
                      </Button>
                    </Link>
                  ) : !user ? (
                    <Link to="/login?tab=register">
                      <Button className="btn-primary btn-press rounded-lg px-8 h-12">
                        <Rocket className="w-4 h-4 mr-2" /> Join as Author
                      </Button>
                    </Link>
                  ) : user?.role === 'author' || user?.role === 'super_admin' ? (
                    <Link to="/editor">
                      <Button className="btn-primary btn-press rounded-lg px-8 h-12">
                        <PenLine className="w-4 h-4 mr-2" /> Write an Article
                      </Button>
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ WHAT'S NEXT ═══ */}
      {!searchQ && (
        <section className="py-16 md:py-20 bg-slate-50" data-testid="whats-next-section">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground text-center mb-10">What's Your Next Step?</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center mb-4">
                  <BookOpen className="w-5 h-5 text-cyan-600" />
                </div>
                <h3 className="font-heading font-bold text-foreground mb-2">For Readers</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Explore deep-dive articles on VMware, Kubernetes & cloud infrastructure.
                </p>
                <Link to="/articles">
                  <Button variant="outline" size="sm" className="btn-press">
                    Browse Articles <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
              
              <div className="bg-white rounded-xl p-6 border shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center mb-4">
                  <PenLine className="w-5 h-5 text-violet-600" />
                </div>
                <h3 className="font-heading font-bold text-foreground mb-2">For Authors</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Share your expertise with 100K+ readers and build your portfolio.
                </p>
                {user?.role === 'reader' ? (
                  <Link to="/apply-author">
                    <Button size="sm" className="btn-primary btn-press">
                      Start Writing <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                ) : user?.role === 'author' || user?.role === 'super_admin' ? (
                  <Link to="/editor">
                    <Button size="sm" className="btn-primary btn-press">
                      Write Article <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login?tab=register">
                    <Button size="sm" className="btn-primary btn-press">
                      Join Now <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ NEWSLETTER ═══ */}
      {!searchQ && (
        <section className="py-16 md:py-20" data-testid="newsletter-section">
          <div className="max-w-xl mx-auto px-6 text-center">
            <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-violet-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-violet-600" />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Stay in the loop</h2>
            <p className="mt-3 text-muted-foreground">
              Weekly infrastructure insights. No spam.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="mt-6 flex gap-3 max-w-md mx-auto" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="h-11 rounded-lg flex-1 border-violet-200 focus:border-violet-500 text-gray-900 bg-white placeholder:text-gray-400"
                data-testid="newsletter-email"
                required
              />
              <Button type="submit" className="btn-primary btn-press rounded-lg h-11 px-6" disabled={subscribing} data-testid="newsletter-submit">
                {subscribing ? '...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
